import { createContext, useContext } from "react";
import { shallowEqual, useSelector } from "react-redux";

import type { AuthZOrganization } from "@features/authz/orgPolicy";
import type { ActivePluginSet } from "@features/plugins/orgPlugins";
import { AuthUser } from "@models/AuthUser";
import { Channel } from "@models/Channel";
import { Territory } from "@models/Territory";
import type { UserRole } from "@models/UserRole";

import type { AuthTokenState } from "./useAuthToken";

export type AuthUserContextValue = AuthUser & {
  channel: Channel | null;
  territory: Territory;
};

/**
 * Derives the context value from the loaded user and the redux-held current
 * territory/channel selection. Shared by AuthUserProvider and the test
 * wrapper so the shape cannot drift.
 */
export function toAuthUserContextValue(
  user: AuthUser,
  currentTerritoryId: string,
  currentChannelId: string
): AuthUserContextValue {
  return {
    ...user,
    territory: user.territories.find((t) => t.id === currentTerritoryId)!,
    channel:
      user.channelUsers.find((cu) => cu.channel.id === currentChannelId)
        ?.channel ?? null,
  };
}

export type PluginQueryState =
  | { status: "pending" }
  | { status: "error"; error: unknown }
  | { status: "success" };

export type AuthRoutingUser = {
  organization: AuthZOrganization;
  role: UserRole;
};

export type AuthRoutingResources = {
  currentUser: AuthRoutingUser | undefined;
  userError: unknown;
  activePluginsQuery: PluginQueryState;
  activePluginSet: ActivePluginSet | undefined;
};

export const AuthUserProviderContext =
  createContext<AuthUserContextValue | null>(null);

export const AuthSessionContext = createContext<AuthTokenState | null>(null);

export const AuthRoutingResourcesContext =
  createContext<AuthRoutingResources | null>(null);

export const useAuthSession = (): AuthTokenState => {
  const context = useContext(AuthSessionContext);
  if (!context) {
    throw new Error("useAuthSession must be used within AuthUserProvider");
  }

  return context;
};

export const useAuthRoutingResources = (): AuthRoutingResources => {
  const context = useContext(AuthRoutingResourcesContext);
  if (!context) {
    throw new Error(
      "useAuthRoutingResources must be used within AuthUserProvider"
    );
  }

  return context;
};

/** Loaded user from AuthUserProviderContext, or null while bootstrapping / signed out. */
export function useOptionalAuthUser(): AuthUserContextValue | null {
  return useContext(AuthUserProviderContext);
}

export const useAuthUser = () => {
  const { channelId, territoryId } = useSelector(
    (state: any) => ({
      channelId: state.currentUser.currentChannelId as string,
      territoryId: state.currentUser.currentTerritoryId as string,
    }),
    shallowEqual
  );
  const context = useOptionalAuthUser();
  if (!context) {
    throw new Error("useAuthUser must be used within a AuthUserProvider");
  }

  return { ...context, channelId, territoryId };
};
